import { createContext, useContext } from 'react';

/* Whether Lumiq is running inside the Suite App Shell, and what the shell handed it.
 *
 * **Module state as well as a React context, because two of the readers are not React.**
 * `shared/api/http.ts` and `shared/auth/session.ts` are plain modules called from query functions,
 * so they read `embeddedRuntime()`; components read the same object through `useEmbedded()`. The shell
 * mounts one product at a time and this remote is mounted at most once, so one slot is enough.
 *
 * **`null` means standalone, and every embedded branch in the app keys on that.** Standalone Lumiq
 * never calls `enterEmbedded`, so none of the behaviour below can reach `npm run dev` or the
 * Dockerfile build — which is the rule the remote work is held to.
 */

export interface EmbeddedRuntime {
  /** "/lumiq": prepended to the server-owned paths so the shell's gateway can route them. */
  readonly apiBase: string;
  /** `ctx.auth.reportSessionExpired`. Replaces the redirect to `/login`, which must never happen here. */
  readonly onSessionExpired: () => void;
  /** `ctx.setTitle`. A product inside the shell never writes `document.title` itself. */
  readonly setTitle: (title: string) => void;
  /** Where portals go: inside the shell's `#lumiq-root`, never `document.body`. */
  readonly portalContainer: HTMLElement;
  /** Whether a node belongs to Lumiq's part of the page, for document-level listeners that must not
   * act on the shell's own elements. */
  readonly owns: (node: Node) => boolean;
}

let runtime: EmbeddedRuntime | null = null;

export function isEmbedded(): boolean {
  return runtime !== null;
}

export function embeddedRuntime(): EmbeddedRuntime | null {
  return runtime;
}

export function enterEmbedded(value: EmbeddedRuntime): void {
  runtime = value;
}

export function leaveEmbedded(): void {
  runtime = null;
}

export const EmbeddedContext = createContext<EmbeddedRuntime | null>(null);

/** The runtime for components, or `null` when Lumiq runs on its own. */
export function useEmbedded(): EmbeddedRuntime | null {
  return useContext(EmbeddedContext);
}
